// Etapa 4: cierre de caja con ventas en la cola local (etapa_4_cierre_caja.md).
// El cierre normal sigue siendo PUT /caja/{id} (CajaCierre). Aquí: contar las pendientes de la caja (H1), enviarlas
// antes de decidir (D4), preparar el cierre provisional dentro del candado del sync (H2, H5), guardarlo en
// cierres_cola ANTES de enviarlo y enviarlo por POST /caja/sync. El sincronizador reintenta los que queden.
import { db } from '@/db/posDb';
import { fetchWrapper } from '@/helpers/fetchWrapper';
import { nuevoUuid } from '@/helpers/uuid';
import { sumarVentasPendientes, resumenPendientes } from '@/helpers/cierreCaja';
import { SYNC, CIERRE } from '@/config/offline';
import { useAuth, useConexion } from '@/stores';
import { conCandado, sincronizarAhora } from '@/services-offline/sincronizador';

const API = process.env.VUE_APP_ROOT_API;
const ahoraIso = () => new Date().toISOString();

function usuarioActual() {
  const auth = useAuth();
  return auth.user ? Number(auth.user.id) : null;
}

// Mismo backoff que las ventas (sincronizador.js): 5 s, 10 s, 20 s, … hasta 5 min, con ±20 %
function espera(intentos) {
  const base = Math.min(SYNC.backoffBaseMs * 2 ** Math.max(0, intentos - 1), SYNC.backoffMaxMs);
  return Math.round(base * (0.8 + Math.random() * 0.4));
}

const ventasEnColaDeCaja = (id) => db.cola_ventas.where('[caja_id+estado]').anyOf([id, 'pendiente'], [id, 'enviando']);

// H1: ventas «de esta caja» = cola_ventas con su caja_id en pendiente o enviando.
// No entran rechazada (solo se nombran, D5), descartada ni sincronizada.
export async function contarPendientesDeCaja(cajaId) {
  const id = Number(cajaId);
  const ventas = await ventasEnColaDeCaja(id).toArray();
  const rechazadas = await db.cola_ventas.where('[caja_id+estado]').equals([id, 'rechazada']).count();
  const userId = usuarioActual();
  const ajenas = ventas.filter((v) => Number(v.user_id) !== userId);
  return {
    pendientes: ventas.length,
    propias: ventas.length - ajenas.length,
    monto: resumenPendientes(ventas).total,
    rechazadas,
    deOtroUsuario: ajenas.length,
    cajerosOtros: [...new Set(ajenas.map((v) => (v.ticket && v.ticket.cajero) || `usuario ${v.user_id}`))],
    ventas,
  };
}

// D4: intenta vaciar la cola de la caja (solo las del usuario con sesión se pueden enviar) hasta CIERRE.esperaEnvioMs.
// alProgresar(k, n) se llama cada CIERRE.progresoMs. Devuelve cuántas quedan.
export async function enviarVentasDeCaja(cajaId, alProgresar) {
  const inicial = (await contarPendientesDeCaja(cajaId)).pendientes;
  const limite = Date.now() + CIERRE.esperaEnvioMs;
  let restantes = inicial;
  const informar = async () => {
    restantes = (await contarPendientesDeCaja(cajaId)).pendientes;
    if (alProgresar) alProgresar(Math.max(0, inicial - restantes), inicial);
  };
  const intervalo = setInterval(() => informar().catch(() => {}), CIERRE.progresoMs);
  try {
    let sinAvance = 0;
    for (;;) {
      const antes = await contarPendientesDeCaja(cajaId);
      if (!antes.propias || Date.now() >= limite) break;
      // Si ya corría un ciclo automático, sincronizarAhora devuelve ese; la vuelta siguiente fuerza el manual
      await Promise.race([sincronizarAhora({ manual: true }), new Promise((r) => setTimeout(r, Math.max(0, limite - Date.now())))]);
      const despues = await contarPendientesDeCaja(cajaId);
      sinAvance = despues.propias < antes.propias ? 0 : sinAvance + 1;
      if (sinAvance >= 2) break; // sin red, servidor caído o rechazos: no tiene sentido seguir esperando
    }
  } finally {
    clearInterval(intervalo);
    await informar();
  }
  return restantes;
}

// Nombres de los métodos de pago (canales del catálogo local) para una fila nueva del detalle
async function metodosPagoLocales() {
  const metodos = [];
  for (const fila of await db.catalogo.toArray()) {
    for (const canal of fila.canales || []) {
      for (const m of canal.metodos_pago || []) {
        if (!metodos.some((x) => Number(x.id) === Number(m.id))) metodos.push({ id: m.id, nombre: m.nombre });
      }
    }
  }
  return metodos;
}

// GET /caja/{id} (Caja Ver Detalle): totales y ventas del servidor. Sin permiso → { sinPermiso: true }
async function leerCajaServidor(cajaId) {
  const r = await fetchWrapper.raw('GET', `${API}/caja/${Number(cajaId)}`, null, { timeoutMs: CIERRE.timeoutMs });
  if (r.status === 401) {
    useConexion().sesionExpirada = true;
    return { error: 'sesion' };
  }
  if (r.ok && r.data && r.data.autorizado === false) return { sinPermiso: true };
  if (!r.ok || !r.data || !r.data.caja) return { error: 'red' };
  return { caja: r.data.caja };
}

// Sin «Caja Ver Detalle»: los totales salen de la fila de GET /caja (Caja Listar, la pantalla del cierre)
async function leerCajaDelListado(cajaId) {
  const r = await fetchWrapper.raw('GET', `${API}/caja`, null, { timeoutMs: CIERRE.timeoutMs });
  const cajas = r.ok && r.data && Array.isArray(r.data.cajas) ? r.data.cajas : [];
  return cajas.find((c) => Number(c.id) === Number(cajaId)) || null;
}

const CAMPOS_TOTALES = ['calculo_total_ventas', 'calculo_total_saldo_caja', 'calculo_total_ventas_metodos_pago',
  'detalle_suma_cobros_x_metodos_pago', 'calculo_total_ingresos', 'calculo_total_egresos'];
const soloTotales = (caja) => Object.fromEntries(CAMPOS_TOTALES.map((k) => [k, caja[k]]));

// Dentro del candado del sync (H2/H5): ningún lote está en vuelo, así que una venta está o en el servidor o en la cola.
// Devuelve los totales del servidor, los ajustados con las pendientes y cantidad_ventas_sesion; o { error }.
export async function prepararCierre(cajaId) {
  const id = Number(cajaId);
  const resultado = await conCandado(async () => {
    const servidor = await leerCajaServidor(id);
    if (servidor.error) return { error: servidor.error };
    let totales = servidor.caja;
    const ventasEnServidor = servidor.caja ? (servidor.caja.ventas || []).length : null; // anuladas incluidas, como el backend
    if (servidor.sinPermiso) {
      totales = await leerCajaDelListado(id);
      if (!totales) return { error: 'red' };
    }
    const pend = await contarPendientesDeCaja(id);
    // Control (D3): el contador del equipo, sin las rechazadas (D5: no cuentan en el cierre)
    const cont = await db.contadores.get(id);
    const control = cont ? Math.max(0, cont.ventas_sesion - pend.rechazadas) : 0;
    let cantidad;
    if (ventasEnServidor === null) {
      cantidad = Math.max(control, pend.pendientes);
      console.warn(`Cierre de la caja #${id}: sin permiso «Caja Ver Detalle» para contar las ventas del servidor; `
        + `cantidad_ventas_sesion = contador del equipo (${cantidad}).`);
    } else {
      cantidad = ventasEnServidor + pend.pendientes;
      if (control > cantidad) {
        console.warn(`Cierre de la caja #${id}: el contador del equipo (${control}) es mayor que servidor + pendientes `
          + `(${ventasEnServidor} + ${pend.pendientes}); se usa el contador.`);
        cantidad = control;
      }
    }
    return {
      pendientes: pend.pendientes,
      rechazadas: pend.rechazadas,
      deOtroUsuario: pend.deOtroUsuario,
      cajerosOtros: pend.cajerosOtros,
      resumen: resumenPendientes(pend.ventas),
      totalesServidor: soloTotales(totales),
      totales: sumarVentasPendientes(totales, pend.ventas, await metodosPagoLocales()),
      cantidad_ventas_sesion: cantidad,
      ventasEnServidor,
    };
  }, { esperar: true });
  return resultado || { error: 'candado' };
}

async function actualizarCierre(uuid, cambios) {
  await db.cierres_cola.update(uuid, cambios);
}

// El cierre se guarda ANTES del primer envío: el uuid_cierre se genera una sola vez y cada reintento manda lo mismo
export async function guardarCierre(cajaId, payload) {
  const uuid = nuevoUuid();
  const fila = {
    uuid_cierre: uuid, caja_id: Number(cajaId), user_id: usuarioActual(),
    payload: { ...payload, uuid_cierre: uuid, caja_id: Number(cajaId) },
    estado: 'pendiente', intentos: 0, creado_en: ahoraIso(), proximo_intento_en: null,
    ultimo_error: null, resultado: null, enviado_en: null,
  };
  await db.cierres_cola.add(fila);
  await useConexion().refrescarContadoresCola();
  return fila;
}

// POST /caja/sync. Devuelve { tipo, ... }: ok | duplicada | ya_cerrada | fecha | invalido | sin_permiso | no_existe |
// error | sesion | red | nada. Red, timeout o 5xx: vuelve a pendiente con backoff; 401: pendiente sin contar intento.
export async function enviarCierre(uuid) {
  const fila = await db.cierres_cola.get(uuid);
  if (!fila || fila.estado !== 'pendiente') return { tipo: 'nada' };
  const conexion = useConexion();
  const res = await fetchWrapper.raw('POST', `${API}/caja/sync`, fila.payload, { timeoutMs: CIERRE.timeoutMs });
  if (res.reloj) conexion.registrarReloj(res.reloj);
  const d = res.data && typeof res.data === 'object' ? res.data : {};
  const rechazar = (motivo) => actualizarCierre(uuid, { estado: 'rechazado', ultimo_error: motivo });
  let resultado;
  if (res.status === 401) {
    conexion.sesionExpirada = true;
    await actualizarCierre(uuid, { ultimo_error: 'Sesión vencida' });
    resultado = { tipo: 'sesion' };
  } else if (res.status === 403) {
    await rechazar('Sin permiso Caja Cerrar');
    resultado = { tipo: 'sin_permiso' };
  } else if (res.status === 422) {
    const errores = d.errors || {};
    await rechazar(d.message || 'Datos inválidos');
    resultado = { tipo: errores.fecha_cierre ? 'fecha' : 'invalido', mensaje: d.message || '' };
  } else if (res.status === 404) {
    await rechazar(d.mensaje || 'La caja no existe');
    resultado = { tipo: 'no_existe' };
  } else if (res.status === 409 || d.status === 'ya_cerrada') {
    const fecha = d.caja && d.caja.fecha_cierre ? d.caja.fecha_cierre : '';
    await rechazar(`La caja ya fue cerrada${fecha ? ` el ${fecha}` : ''}`);
    await limpiarCajaLocal(fila.caja_id);
    resultado = { tipo: 'ya_cerrada', fecha };
  } else if (res.ok && (d.status === 'ok' || d.status === 'duplicada')) {
    await actualizarCierre(uuid, {
      estado: 'enviado', enviado_en: ahoraIso(), ultimo_error: null,
      resultado: { status: d.status, es_provisional: !!d.es_provisional },
    });
    await limpiarCajaLocal(fila.caja_id);
    resultado = { tipo: d.status, es_provisional: !!d.es_provisional, mensaje: d.mensaje || '' };
  } else if (res.ok && d.status === 'error') {
    await rechazar(d.mensaje || 'Error al cerrar la caja');
    resultado = { tipo: 'error', mensaje: d.mensaje || '' };
  } else {
    const intentos = (fila.intentos || 0) + 1;
    await actualizarCierre(uuid, {
      intentos, ultimo_error: res.status ? `HTTP ${res.status}` : 'Sin conexión',
      proximo_intento_en: new Date(Date.now() + espera(intentos)).toISOString(),
    });
    resultado = { tipo: 'red' };
  }
  await conexion.refrescarContadoresCola();
  return resultado;
}

// Para el sincronizador, al final de cada ciclo (detrás de las ventas): solo los cierres del usuario con sesión
// (el backend atribuye el cierre al token, como las ventas)
export async function enviarCierresPendientes(manual = false) {
  const userId = usuarioActual();
  const ahora = Date.now();
  const filas = (await db.cierres_cola.where('estado').equals('pendiente').toArray())
    .filter((c) => Number(c.user_id) === userId)
    .filter((c) => manual || !c.proximo_intento_en || Date.parse(c.proximo_intento_en) <= ahora)
    .sort((a, b) => a.creado_en.localeCompare(b.creado_en));
  for (const c of filas) {
    const r = await enviarCierre(c.uuid_cierre);
    if (r.tipo === 'red' || r.tipo === 'sesion') break;
  }
}

// Próximos reintentos de cierres del usuario (para programar el temporizador del sincronizador)
export async function proximosIntentosCierres() {
  const userId = usuarioActual();
  return (await db.cierres_cola.where('estado').equals('pendiente').toArray())
    .filter((c) => Number(c.user_id) === userId)
    .map((c) => (c.proximo_intento_en ? Date.parse(c.proximo_intento_en) : Date.now()));
}

// El cierre pendiente de una caja (si lo hay): un segundo cierre de la misma caja reintenta este
export async function cierrePendienteDeCaja(cajaId) {
  const filas = await db.cierres_cola.where('caja_id').equals(Number(cajaId)).toArray();
  return filas.find((c) => c.estado === 'pendiente') || null;
}

// Para las etiquetas de CajaList: caja_id → cierre pendiente o, si no, el último rechazado
export async function cierresPorCaja() {
  const mapa = {};
  const filas = (await db.cierres_cola.where('estado').anyOf('pendiente', 'rechazado').toArray())
    .sort((a, b) => a.creado_en.localeCompare(b.creado_en));
  for (const c of filas) {
    if (!mapa[c.caja_id] || c.estado === 'pendiente' || mapa[c.caja_id].estado !== 'pendiente') mapa[c.caja_id] = c;
  }
  return mapa;
}

// H3: al cerrar, el POS sin red no debe seguir vendiendo en esta caja. El catálogo se conserva; solo caja = null.
// contadores: se borra la fila si a la caja no le quedan ventas por enviar (si quedan, la borra purgarCierres).
export async function limpiarCajaLocal(cajaId) {
  const id = Number(cajaId);
  await db.catalogo.toCollection().modify((f) => {
    if (f.caja && Number(f.caja.id) === id) f.caja = null;
  });
  if (!(await ventasEnColaDeCaja(id).count())) await db.contadores.delete(id);
}

// Purga (la llama purgarCola cada hora): contadores de cajas cerradas en este equipo sin ventas por enviar, y
// cierres enviados con más de SYNC.diasPurga días. Los rechazados no se borran solos.
export async function purgarCierres() {
  // Solo 'enviado': un cierre rechazado (p. ej. por la hora del equipo) deja la caja abierta y su correlativo sigue
  const cerradas = new Set((await db.cierres_cola.where('estado').equals('enviado').toArray()).map((c) => Number(c.caja_id)));
  for (const id of cerradas) {
    if (!(await ventasEnColaDeCaja(id).count())) await db.contadores.delete(id);
  }
  const limite = Date.now() - SYNC.diasPurga * 86400000;
  await db.cierres_cola.where('estado').equals('enviado')
    .and((c) => !!c.enviado_en && Date.parse(c.enviado_en) < limite).delete();
}
